import {mergeAttributes, Node} from "@tiptap/core";
import {ReactNodeViewRenderer} from "@tiptap/react";
import {parseJsonAttribute} from "../shared/blockAttributes";
import PrintoutFolioTableNodeView from "./PrintoutFolioTableNodeView";
import {DEFAULT_FOLIO_ATTRS} from "./printoutFolioTableOptions";
import type {
    FolioColumn,
    FolioSort,
    FolioTotal,
} from "./printoutFolioTableOptions";

const jsonAttribute = <T,>(dataAttribute: string, fallback: T) => ({
    default: fallback,
    parseHTML: (element: HTMLElement) => (
        parseJsonAttribute<T>(element.getAttribute(dataAttribute), dataAttribute) ?? fallback
    ),
    renderHTML: (attributes: Record<string, unknown>) => ({
        [dataAttribute]: JSON.stringify(attributes[dataAttribute.replace(/^data-/, "")]),
    }),
});

const booleanAttribute = (dataAttribute: string, fallback: boolean, attributeName: string) => ({
    default: fallback,
    parseHTML: (element: HTMLElement) => {
        const value = element.getAttribute(dataAttribute);
        if (value === null) return fallback;

        return value !== "false";
    },
    renderHTML: (attributes: Record<string, unknown>) => ({
        [dataAttribute]: attributes[attributeName] === true ? "true" : "false",
    }),
});

export const PrintoutFolioTableNode = Node.create({
    name: "printoutFolioTable",

    group: "block",
    atom: true,
    selectable: true,
    draggable: true,

    addAttributes() {
        return {
            columns: jsonAttribute<FolioColumn[]>("data-columns", DEFAULT_FOLIO_ATTRS.columns),
            sort: jsonAttribute<FolioSort>("data-sort", DEFAULT_FOLIO_ATTRS.sort),
            totals: jsonAttribute<FolioTotal[]>("data-totals", DEFAULT_FOLIO_ATTRS.totals),
            // Preview-only values are never serialized.
            resolved: {default: null, rendered: false},
            excludeSyntheticRows: booleanAttribute(
                "data-exclude-synthetic-rows",
                DEFAULT_FOLIO_ATTRS.excludeSyntheticRows,
                "excludeSyntheticRows",
            ),
            groupByDate: booleanAttribute("data-group-by-date", DEFAULT_FOLIO_ATTRS.groupByDate, "groupByDate"),
        };
    },

    parseHTML() {
        return [
            {
                tag: 'div[data-type="printout-folio-table"]',
            },
        ];
    },

    renderHTML({HTMLAttributes}) {
        return [
            "div",
            mergeAttributes(HTMLAttributes, {
                "data-type": "printout-folio-table",
                class: "printout-rendered-folio",
            }),
        ];
    },

    addNodeView() {
        return ReactNodeViewRenderer(PrintoutFolioTableNodeView, {
            trackNodeViewPosition: true,
        });
    },
});
